import { Dropdown, type MenuProps } from "antd";
import { CopyOutlined, DeleteOutlined } from "@ant-design/icons";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import "./index.less";

type MessageDeleteMenuProps = {
  canDelete: boolean;
  isDeleting?: boolean;
  onDelete: () => void;
  onCopy?: () => void;
  showCopy?: boolean;
  copyDisabled?: boolean;
  enableLongPress?: boolean;
  children?: React.ReactNode;
};

const MOBILE_MEDIA_QUERY = "(max-width: 768px), (pointer: coarse)";
const LONG_PRESS_DELAY_MS = 450;

const withComposedHandler = <E,>(original: ((event: E) => void) | undefined, next: (event: E) => void) => {
  return (event: E) => {
    original?.(event);
    next(event);
  };
};

const MessageDeleteMenu: React.FC<MessageDeleteMenuProps> = ({
  canDelete,
  isDeleting = false,
  onDelete,
  onCopy,
  showCopy = false,
  copyDisabled = false,
  enableLongPress = false,
  children,
}) => {
  const { t } = useTranslation();
  const [isMobileDevice, setIsMobileDevice] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const longPressTimeoutRef = useRef<number | null>(null);
  const longPressTriggeredRef = useRef(false);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;

    const mediaQuery = window.matchMedia(MOBILE_MEDIA_QUERY);
    const handleMediaQueryChange = (event: MediaQueryListEvent) => {
      setIsMobileDevice(event.matches);
      if (!event.matches) {
        setIsOpen(false);
      }
    };

    setIsMobileDevice(mediaQuery.matches);
    mediaQuery.addEventListener("change", handleMediaQueryChange);

    return () => {
      mediaQuery.removeEventListener("change", handleMediaQueryChange);
      if (longPressTimeoutRef.current) {
        window.clearTimeout(longPressTimeoutRef.current);
      }
    };
  }, []);

  const clearLongPressTimer = () => {
    if (longPressTimeoutRef.current) {
      window.clearTimeout(longPressTimeoutRef.current);
      longPressTimeoutRef.current = null;
    }
  };

  const handleTouchStart: React.TouchEventHandler<HTMLDivElement> = () => {
    if (!enableLongPress || !isMobileDevice) return;

    longPressTriggeredRef.current = false;
    clearLongPressTimer();
    longPressTimeoutRef.current = window.setTimeout(() => {
      longPressTriggeredRef.current = true;
      setIsOpen(true);
      longPressTimeoutRef.current = null;
    }, LONG_PRESS_DELAY_MS);
  };

  const handleTouchEnd: React.TouchEventHandler<HTMLDivElement> = (event) => {
    if (longPressTriggeredRef.current) {
      // Only suppress default tap behavior after a true long-press.
      event.preventDefault();
    }
    longPressTriggeredRef.current = false;
    clearLongPressTimer();
  };

  const handleTouchMove: React.TouchEventHandler<HTMLDivElement> = () => {
    longPressTriggeredRef.current = false;
    clearLongPressTimer();
  };

  const handleTouchCancel: React.TouchEventHandler<HTMLDivElement> = () => {
    longPressTriggeredRef.current = false;
    clearLongPressTimer();
  };

  const handleContextMenu: React.MouseEventHandler<HTMLDivElement> = (event) => {
    if (!enableLongPress || !isMobileDevice) return;
    event.preventDefault();
    setIsOpen(true);
  };

  const items: MenuProps["items"] = [
    ...(showCopy
      ? [
          {
            key: "copy",
            label: (
              <span className="message-action-menu-row">
                <CopyOutlined className="message-action-menu-icon" />
                <span className="message-action-menu-label">{t("copy")}</span>
              </span>
            ),
            disabled: copyDisabled,
          },
        ]
      : []),
    {
      key: "delete",
      label: (
        <span className="message-action-menu-row">
          <DeleteOutlined className="message-action-menu-icon" />
          <span className="message-action-menu-label">{isDeleting ? t("please-wait") : t("Delete")}</span>
        </span>
      ),
      disabled: !canDelete || isDeleting,
      danger: true,
    },
  ];

  const handleMenuClick: MenuProps["onClick"] = ({ key }) => {
    if (key === "copy" && !copyDisabled && onCopy) {
      onCopy();
    }
    if (key === "delete" && canDelete && !isDeleting) {
      onDelete();
    }
    setIsOpen(false);
  };

  if (enableLongPress && children) {
    const longPressProps = {
      onTouchStart: handleTouchStart,
      onTouchEnd: handleTouchEnd,
      onTouchCancel: handleTouchCancel,
      onTouchMove: handleTouchMove,
      onContextMenu: handleContextMenu,
      className: "message-long-press-target",
      style: {
        userSelect: "none" as const,
        WebkitUserSelect: "none" as const,
        WebkitTouchCallout: "none" as const,
      },
    };

    const triggerNode = React.isValidElement(children) ? (
      React.cloneElement(children as React.ReactElement<any>, {
        onTouchStart: withComposedHandler((children as React.ReactElement<any>).props.onTouchStart, longPressProps.onTouchStart),
        onTouchEnd: withComposedHandler((children as React.ReactElement<any>).props.onTouchEnd, longPressProps.onTouchEnd),
        onTouchCancel: withComposedHandler((children as React.ReactElement<any>).props.onTouchCancel, longPressProps.onTouchCancel),
        onTouchMove: withComposedHandler((children as React.ReactElement<any>).props.onTouchMove, longPressProps.onTouchMove),
        onContextMenu: withComposedHandler((children as React.ReactElement<any>).props.onContextMenu, longPressProps.onContextMenu),
        className: [(children as React.ReactElement<any>).props.className, longPressProps.className].filter(Boolean).join(" "),
        style: {
          ...longPressProps.style,
          ...((children as React.ReactElement<any>).props.style || {}),
        },
      })
    ) : (
      <div
        className={longPressProps.className}
        style={longPressProps.style}
        onTouchStart={longPressProps.onTouchStart}
        onTouchEnd={longPressProps.onTouchEnd}
        onTouchCancel={longPressProps.onTouchCancel}
        onTouchMove={longPressProps.onTouchMove}
        onContextMenu={longPressProps.onContextMenu}
      >
        {children}
      </div>
    );

    return (
      <Dropdown
        menu={{ items, onClick: handleMenuClick, className: "message-action-menu" }}
        trigger={[]}
        placement="topRight"
        overlayClassName="message-action-dropdown"
        open={isMobileDevice ? isOpen : false}
        onOpenChange={(nextOpen) => {
          if (isMobileDevice) {
            setIsOpen(nextOpen);
          }
        }}
      >
        {triggerNode}
      </Dropdown>
    );
  }

  return (
    <Dropdown
      menu={{ items, onClick: handleMenuClick, className: "message-action-menu" }}
      trigger={["click"]}
      placement="topRight"
      overlayClassName="message-action-dropdown"
    >
      <button type="button" className="message-action-trigger" aria-label={t("more") || "more actions"}>
        <span className="arrow" />
      </button>
    </Dropdown>
  );
};

export default MessageDeleteMenu;
